import { useMemo } from 'react';

import { CenterFlex, VFlex } from '@/components/base';
import { MainText } from '@/components/Typography/NamedTypo';
import { CHART_GRAPH, CHART_TYPE } from '@/entities/chart/model/constants';
import type { DashboardCardInfoT } from '@/features/Dashboard/types';

import CrossStackedBarChart from './Cross/CrossStackedBarChart';
import CrossTableChart from './Cross/CrossTableChart';
import BarChart from './Single/BarChart';
import ComboChart from './Single/ComboChart';
import LineChart from './Single/LineChart';
import PieChart from './Single/PieChart';
import StackedAreaChart from './Single/StackedAreaChart';
import TimeSeriesStackedBarChart from './Single/TimeSeriesStackedBarChart';
import TimeSeriesTableChart from './Single/TimeSeriesTable';
import TotalTable from './Single/TotalTable';

type ChartRendererProps = {
  chart: ChartBase & { name: string };
  tableData: ChartTableRes;
  onHistoricalChange: (value: boolean) => void;
  height?: number;
  dashboardCardInfo?: DashboardCardInfoT;
  yAxisTitle?: string;
};

const ChartRenderer = (props: ChartRendererProps) => {
  const { chart, tableData, dashboardCardInfo } = props;

  const Renderer = useMemo(() => {
    if (!tableData) return <EmptyChart />;

    if (chart.type === CHART_TYPE.CROSS_AXIS) {
      let CrossChart;
      switch (chart.graph) {
        case CHART_GRAPH.BAR_STACKED:
          CrossChart = CrossStackedBarChart;
          break;
        case CHART_GRAPH.TABLE:
          CrossChart = CrossTableChart;
          break;
        default:
          throw new Error('Invalid graph type');
      }
      return (
        <CrossChart
          key={JSON.stringify(chart)}
          tableData={tableData as ChartCrossTableRes}
          chart={chart}
          height={props.height}
          dashboardCardInfo={dashboardCardInfo}
        />
      );
    }
    if (chart.type === CHART_TYPE.SINGLE_AXIS) {
      let SingleChart;
      switch (chart.graph) {
        case CHART_GRAPH.BAR:
          SingleChart = BarChart;
          break;
        case CHART_GRAPH.LINE:
          SingleChart = LineChart;
          break;
        case CHART_GRAPH.PIE:
          SingleChart = PieChart;
          break;
        case CHART_GRAPH.COMBO_CHART:
          SingleChart = ComboChart;
          break;
        case CHART_GRAPH.BAR_STACKED:
          SingleChart = TimeSeriesStackedBarChart;
          break;
        case CHART_GRAPH.AREA_STACKED:
          SingleChart = StackedAreaChart;
          break;
        case CHART_GRAPH.TABLE:
          if (chart.historical.value) {
            SingleChart = TimeSeriesTableChart;
            break;
          }
          SingleChart = TotalTable;
          break;
        default:
          throw new Error('Invalid graph type');
      }
      return (
        <SingleChart
          key={JSON.stringify(chart)}
          tableData={tableData as ChartSingleTableRes}
          chart={chart}
          onHistoricalChange={props.onHistoricalChange}
          height={props.height}
          dashboardCardInfo={dashboardCardInfo}
          yAxisTitle={props.yAxisTitle}
        />
      );
    }
    throw new Error('Invalid chart type');
  }, [
    chart,
    dashboardCardInfo,
    props.height,
    props.onHistoricalChange,
    props.yAxisTitle,
    tableData,
  ]);

  return (
    <VFlex f={1} style={{ overflow: 'hidden', height: '100%' }}>
      {Renderer}
    </VFlex>
  );
};

const EmptyChart = () => {
  return (
    <CenterFlex style={{ height: '100%' }}>
      <MainText dim>
        Select data sets and apply various filters to create your own chart.
      </MainText>
    </CenterFlex>
  );
};

export default ChartRenderer;
